import React, { useLayoutEffect, useEffect, useMemo } from 'react'
import { ScrollView } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import HabitActions from 'APP/Redux/HabitsRedux'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import { Habits } from '@dialogue/services'
import type { UserHabitWithStatus } from '@dialogue/services/dist/habits/habits/model'
import type { Profile, RelatedContentInner } from '@dialogue/cms'
import Toast from 'react-native-toast-message'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import ButtonShare from 'APP/Components/ButtonShare'
import { useShareDeepLinkFromConfig } from 'APP/Hooks/Share'
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'
import { Colors, Metrics, Images } from 'APP/Themes'
import {
  Container,
  LoadingIndicator,
  RefreshButton,
  HeaderImage,
  ContentContainer,
  Title,
  SubheaderContainer,
  Subheader,
  AddHabitCta,
  BodyContainer,
  ExpertCard,
  ExpertCaptionContainer,
  ExpertCaptionText,
  ExpertContentContainer,
  ExpertTitle,
  ExpertSubtitle,
  ExpertAvatar,
  ExpertDetailsContainer,
} from './style'
import FactCheckedIcon from 'APP/Images/fact-checked.svg'
import sprigClient from 'APP/Services/Sprig'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'

export type HabitContentData = {
  relatedContent?: RelatedContentInner[]
  expert?: Profile
}

export type HabitDetailProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'habitDetailScreen'
> & {
  id?: string
  hideCTAs?: boolean
}

const HabitDetail: React.FC<HabitDetailProps> = (props) => {
  const { navigation, route } = props
  const { id, hideCTAs = false } = { ...route?.params, ...props }
  const analyticsName = route?.name
  const insets = useSafeAreaInsets()
  const dispatch = useDispatch()

  const { loading, retry, data } = useSelector((state) => state.habits.habit) as unknown as {
    loading: boolean
    retry: boolean
    data: UserHabitWithStatus
  }
  const startHabitState = useSelector((state) => state.habits.startHabit)
  const habitContentState = useSelector((state) => state.habits.habitContent[id as string]) as
    | { loading: boolean; retry: boolean; data: HabitContentData }
    | undefined

  const shareHabitDetails = useShareDeepLinkFromConfig(
    'HABIT_DETAILS_SCREEN_DEEP_LINK_URL',
    {
      services: 'wellness_habits',
      id,
      share: true,
    },
    { analyticsName }
  )

  useEffect(() => {
    if (id) {
      getHabit()
      getHabitContent()
    }
  }, [id])

  useEffect(() => {
    if (data?.identifier) {
      const addedState = data.state === Habits.Habits.Types.State.Ongoing ? 'Added' : 'Not added'
      Analytics.trackScreen(
        `habitDetailScreen: ${id} : ${data.title[I18n.baseLocale]} : ${addedState}`
      )
    }
  }, [id, data])
  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <ButtonShare
          analyticsName={analyticsName}
          onPress={() => shareHabitDetails.share()}
          testID="shareHabitDetailsBtn"
        />
      ),
    })
  }, [analyticsName, shareHabitDetails, navigation])

  const setEditActions = () =>
    dispatch(
      HabitActions.setHabitListDeferredActions(true, {
        id,
        showModifyModal: true,
      })
    )
  const getHabit = () => dispatch(HabitActions.getSingleHabit(id))
  const getHabitContent = () => dispatch(HabitActions.getHabitContent(id))

  const startHabit = () => {
    const hasSupportingContent = (habitContentState?.data?.relatedContent?.length ?? 0) > 0

    Analytics.trackEvent('button_click', {
      button_value: `addHabit: ${id} : ${data.title[I18n.baseLocale]}`,
      trigger: analyticsName,
    })
    sprigClient.track('HABIT_ADDED')
    dispatch(HabitActions.startHabit(id, hasSupportingContent))
  }
  const resetStartHabit = () => dispatch(HabitActions.resetStartHabit())

  const editHabit = () => {
    setEditActions()
    navigateToWellnessCenterHabits()
  }

  /**
   * Usag type: 'reset' to replace & unmount the current screen
   * with a tabbar
   */
  const navigateToWellnessCenterHabits = () => {
    navigation.goBack()
    // Delay the navigation to allow the modal to close
    setTimeout(() => {
      // @ts-expect-error: RootParamList.tabbar is typed as `undefined` in custom.d.ts, but this targets a nested tab via react-navigation's `{ screen }` convention
      navigation.navigate('tabbar', { screen: 'todayTab' })
    }, 300)
  }

  useEffect(() => {
    if (startHabitState.retry) {
      Toast.show({ text1: I18n.t('HabitDetailScreen.retryHabitCta') })
    }

    if (startHabitState.started) {
      navigateToWellnessCenterHabits()
    }

    return () => {
      resetStartHabit()
    }
  }, [startHabitState])

  const isHabitOngoing = useMemo(() => {
    return data?.state === Habits.Habits.Types.State.Ongoing
  }, [data])

  return (
    <Container paddingBottom={insets.bottom}>
      {loading && <LoadingIndicator size="large" color={Colors.text} testID="loading" />}
      {!loading && data?.identifier && (
        <ScrollView>
          <HeaderImage
            source={{ uri: data.image.src }}
            defaultSource={Images.contentLoading}
            resizeMode="cover"
          />
          <ContentContainer>
            <Title testID="title" variant="h1" color="text">
              {data.title[I18n.baseLocale]}
            </Title>
            {data.state !== Habits.Habits.Types.State.Pending &&
              I18n.exists(`ExploreHabitsScreen.habitState.${data.state}`) && (
                <SubheaderContainer>
                  <MaterialIcon color={Colors.accent} size={Metrics.icons.tiny} name="done" />
                  <Subheader variant="bodySmall" color="text">
                    {I18n.t(`ExploreHabitsScreen.habitState.${data.state}`)}
                  </Subheader>
                </SubheaderContainer>
              )}
            {!hideCTAs && isHabitOngoing && (
              <AddHabitCta title={I18n.t('HabitDetailScreen.editHabitCta')} onPress={editHabit} />
            )}
            {!hideCTAs && !isHabitOngoing && (
              <AddHabitCta
                title={I18n.t('HabitDetailScreen.addHabitCta')}
                onPress={startHabit}
                disabled={startHabitState.loading}
                showActivityIndicator={startHabitState.loading}
              />
            )}
            <BodyContainer>
              <DialogueMarkdown
                markdown={data.description[I18n.baseLocale]}
                styles={{}}
                containerStyles={{}}
              />
              {habitContentState?.data?.expert && (
                <ExpertCard testID="expertTile">
                  <ExpertCaptionContainer>
                    <FactCheckedIcon
                      // @ts-expect-error: FactCheckedIcon SVG accepts custom fillLine/accentFillLine, not in SvgProps
                      fillLine={Colors.text}
                      accentFillLine={Colors.accent}
                      width={Metrics.icons.small}
                      height={Metrics.icons.small}
                    />
                    <ExpertCaptionText variant="captionBold" color="text">
                      {I18n.t('HabitDetailScreen.expertCaption')}
                    </ExpertCaptionText>
                  </ExpertCaptionContainer>
                  <ExpertContentContainer>
                    <ExpertAvatar
                      source={{
                        // @ts-expect-error: Profile.profile_picture is typed as `Media | null` in @dialogue/cms, but this section only renders once expert data is present
                        uri: habitContentState.data.expert?.profile_picture.url,
                      }}
                    />
                    <ExpertDetailsContainer>
                      <ExpertTitle variant="bodyLargeBold" color="text">
                        {habitContentState.data.expert?.name}
                      </ExpertTitle>
                      <ExpertSubtitle variant="captionBold" color="text">
                        {habitContentState.data.expert?.job_title}
                      </ExpertSubtitle>
                    </ExpertDetailsContainer>
                  </ExpertContentContainer>
                </ExpertCard>
              )}
            </BodyContainer>
          </ContentContainer>
        </ScrollView>
      )}
      {!loading && retry && (
        <RefreshButton onPress={getHabit} analyticsName="Refresh" testID="retry">
          <MaterialIcon name="refresh" size={Metrics.icons.large} color={Colors.text} />
        </RefreshButton>
      )}
    </Container>
  )
}

export default HabitDetail
